import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { LOGO_SVG, doctorDisplay, rxLegalDisclaimer } from "../lib/clinic";
import { CheckCircle2, XCircle, ShieldCheck } from "lucide-react";

/**
 * Public Rx-verification page — reachable by scanning the QR code on any
 * printed prescription. No auth required. Displays a lightweight
 * authenticity confirmation without leaking PHI.
 */
export default function VerifyRx() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const locator = params.get("t") || "";   // MT-12B: server-issued tenant locator, forwarded verbatim
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    axios
      .get(`${base}/api/rx/verify/${encodeURIComponent(token)}`, { params: locator ? { t: locator } : {} })
      .then((r) => setState({ loading: false, data: r.data, error: null }))
      .catch((e) => setState({ loading: false, data: null, error: e?.message || "Network error" }));
  }, [token, locator]);

  // MT-11C.4: every clinic detail on this PUBLIC page comes from the verify
  // response (the tenant the server resolved from the signed QR locator) —
  // never from the anonymous bootstrap profile, which cannot know the tenant.
  const clinicName = state.data?.clinic || "Prescription verification";
  const clinicPhones = state.data?.clinic_phones || [];
  const clinicAddress = state.data?.clinic_address || "";
  useEffect(() => {
    document.title = state.data?.clinic ? `${state.data.clinic} — Prescription verification` : "Prescription verification";
  }, [state.data]);

  const issued = state.data?.issued_at ? new Date(state.data.issued_at).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  }) : "—";
  const valid = state.data?.valid;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center py-10 px-4">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="rx-verify-card">
        <header className="flex items-center gap-3 p-5 border-b border-slate-100 bg-slate-900 text-white">
          <div className="w-10 h-14 flex-shrink-0">{LOGO_SVG}</div>
          <div className="min-w-0 flex-1">
            <div className="font-heading text-base font-bold leading-tight" data-testid="verify-clinic-name">{clinicName}</div>
            <div className="text-[10px] text-slate-300 mt-0.5">Prescription Authenticity Check</div>
          </div>
        </header>

        <div className="p-6">
          {state.loading && <div className="text-sm text-slate-500" data-testid="rx-verify-loading">Verifying prescription…</div>}
          {state.error && (
            <div className="flex items-start gap-3" data-testid="rx-verify-error">
              <XCircle className="text-rose-600 mt-0.5" size={20} />
              <div>
                <div className="font-semibold text-rose-700">Verification unavailable</div>
                <div className="text-xs text-slate-500 mt-1">{state.error}</div>
              </div>
            </div>
          )}
          {state.data && !valid && (
            <div className="flex items-start gap-3" data-testid="rx-verify-invalid">
              <XCircle className="text-rose-600 mt-0.5" size={22} />
              <div>
                <div className="font-semibold text-rose-700">Not a genuine prescription</div>
                <div className="text-sm text-slate-600 mt-1">{state.data.message}</div>
              </div>
            </div>
          )}
          {state.data && valid && (
            <div data-testid="rx-verify-valid">
              <div className="flex items-start gap-3 mb-5">
                <CheckCircle2 className="text-emerald-600 mt-0.5" size={22} />
                <div>
                  <div className="font-semibold text-emerald-700">Genuine prescription</div>
                  <div className="text-xs text-slate-500 mt-1">{state.data.message}</div>
                </div>
              </div>

              <dl className="grid grid-cols-3 gap-y-2 gap-x-3 text-[12px] border-t border-slate-100 pt-4">
                <dt className="text-slate-500 uppercase tracking-wider text-[9px] font-semibold col-span-1">Rx ID</dt>
                <dd className="col-span-2 font-mono text-slate-800">{state.data.prescription_id}</dd>

                <dt className="text-slate-500 uppercase tracking-wider text-[9px] font-semibold">Issued</dt>
                <dd className="col-span-2 text-slate-800 tabular">{issued}</dd>

                <dt className="text-slate-500 uppercase tracking-wider text-[9px] font-semibold">Doctor</dt>
                <dd className="col-span-2 text-slate-800">{doctorDisplay(state.data.doctor)}</dd>

                <dt className="text-slate-500 uppercase tracking-wider text-[9px] font-semibold">Patient</dt>
                <dd className="col-span-2 text-slate-800">{state.data.patient_initials} (initials only)</dd>

                <dt className="text-slate-500 uppercase tracking-wider text-[9px] font-semibold">Medicines</dt>
                <dd className="col-span-2 text-slate-800">
                  {state.data.medicines_count} item(s)
                  {state.data.medicines_sample?.length ? (
                    <span className="block text-[10px] text-slate-500 mt-0.5">
                      Sample: {state.data.medicines_sample.slice(0, 4).join(" · ")}
                      {state.data.medicines_sample.length > 4 ? " …" : ""}
                    </span>
                  ) : null}
                </dd>
              </dl>

              <div className="mt-5 p-3 rounded-md bg-emerald-50 border border-emerald-100 flex items-start gap-2">
                <ShieldCheck className="text-emerald-600 mt-0.5 flex-shrink-0" size={14} />
                <div className="text-[11px] text-emerald-800 leading-relaxed">
                  Detailed doses, timing and duration are visible only on the original printed prescription — not shown here to protect patient privacy.
                </div>
              </div>

              {/* Legal expiry / dispensing disclaimer — small yet bold, so
                  pharmacies scanning the QR know not to dispense past the
                  follow-up date without renewed authorization. */}
              <p className="mt-4 text-[11px] font-bold text-slate-700 leading-relaxed" data-testid="rx-legal-disclaimer">
                {rxLegalDisclaimer("en")}
              </p>
            </div>
          )}
        </div>

        {(clinicAddress || clinicPhones.length > 0) && (
          <footer className="px-6 py-3 border-t border-slate-100 bg-slate-50 text-[10px] text-slate-500 leading-snug" data-testid="verify-clinic-contact">
            {clinicAddress && <div>{clinicAddress}</div>}
            {clinicPhones.length > 0 && <div className="mt-1 tabular">{clinicPhones.join(" · ")}</div>}
          </footer>
        )}
      </div>

      <div className="mt-6 text-[10px] text-slate-400 max-w-md text-center">
        If this prescription looks suspicious or the details don't match, please contact {clinicName} directly{clinicPhones.length > 0 ? " using the phone numbers above" : ""}.
      </div>
    </div>
  );
}
